/**
 * Gestión de Productos - Sistema Unificado
 *
 * Este componente implementa la gestión completa de productos/packs utilizando
 * el sistema unificado de componentes administrativos.
 *
 * Funcionalidades:
 * - CRUD completo de productos
 * - Activar/desactivar productos
 * - Filtros avanzados y búsqueda
 * - Validaciones robustas
 * - Modal de visualización detallada
 * - Manejo de campos complejos (precios, sesiones, fechas)
 */

import { useState, useEffect, useCallback } from 'react';
import AdminCRUDLayout from '../../components/admin/AdminCRUDLayout';
import UnifiedDataTable from '../../components/admin/UnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import StandardForm from '../../components/admin/StandardForm';
import productService from '../../services/productService';
import torneoService from '../../services/torneoService';
import './ProductManagementUnified.css';

const ProductManagementUnified = () => {
  // Estados principales
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Estados de formulario
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [torneoOptions, setTorneoOptions] = useState([]);
  const [sedeOptions, setSedeOptions] = useState([]);

  // Estados de filtros
  const [filters, setFilters] = useState({
    search: '',
    active: '',
    featured: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    includesEquipment: '',
    hasAgeRestrictions: '',
    performance: '',
    availability: ''
  });

  /**
   * Configuración de filtros para la tabla
   */
  const filterConfig = [
    {
      key: 'search',
      type: 'search',
      placeholder: 'Buscar por nombre, código o descripción...',
      icon: 'search'
    },
    {
      key: 'category',
      type: 'select',
      label: 'Categoría',
      options: [
        { value: '', label: 'Todas las categorías' },
        { value: 'individual', label: 'Individual' },
        { value: 'pack', label: 'Pack' },
        { value: 'premium', label: 'Premium' },
        { value: 'special', label: 'Especial' }
      ]
    },
    {
      key: 'active',
      type: 'select',
      label: 'Estado',
      options: [
        { value: '', label: 'Todos los estados' },
        { value: 'true', label: 'Activos' },
        { value: 'false', label: 'Inactivos' }
      ]
    },
    {
      key: 'featured',
      type: 'select',
      label: 'Destacados',
      options: [
        { value: '', label: 'Todos' },
        { value: 'true', label: 'Solo destacados' },
        { value: 'false', label: 'No destacados' }
      ]
    },
    {
      key: 'includesEquipment',
      type: 'select',
      label: 'Equipamiento',
      options: [
        { value: '', label: 'Todos' },
        { value: 'true', label: 'Con equipamiento' },
        { value: 'false', label: 'Sin equipamiento' }
      ]
    },
    {
      key: 'performance',
      type: 'select',
      label: 'Rendimiento',
      options: [
        { value: '', label: 'Todos' },
        { value: 'high', label: 'Alto rendimiento' },
        { value: 'medium', label: 'Rendimiento medio' },
        { value: 'low', label: 'Bajo rendimiento' },
        { value: 'none', label: 'Sin ventas' }
      ]
    },
    {
      key: 'priceRange',
      type: 'range',
      label: 'Rango de precio',
      min: 0,
      max: 200,
      step: 5,
      format: 'currency'
    }
  ];

  /**
   * Configuración de columnas para la tabla
   */
  const columns = [
    {
      key: 'code',
      label: 'Código',
      sortable: true,
      render: (row) => (
        <div className="product-code-container">
          <span className="material-icons product-icon">inventory_2</span>
          <span className="product-code">{row.code}</span>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Producto',
      sortable: true,
      render: (row) => (
        <div className="product-info">
          <div className="product-name">{row.name}</div>
          <div className="product-description">{row.description?.substring(0, 60)}...</div>
          <div className="product-badges">
            {row.featured && (
              <span className="badge badge-featured">
                <span className="material-icons">star</span>
                Destacado
              </span>
            )}
            {row.includesEquipment && (
              <span className="badge badge-equipment">
                <span className="material-icons">sports_soccer</span>
                Equipamiento
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'sede',
      label: 'Sede',
      sortable: false,
      render: (row) => (
        <div className="sede-info">
          {row.sede ? (
            <>
              <div className="sede-name">{row.sede.nombre}</div>
              {row.sede.poblacion && (
                <div className="sede-poblacion">{row.sede.poblacion}</div>
              )}
            </>
          ) : (
            <span className="sede-unknown">—</span>
          )}
        </div>
      )
    },
    {
      key: 'price',
      label: 'Precio',
      sortable: true,
      render: (row) => (
        <div className="price-info">
          <div className="product-price">{formatPrice(row.price, row.currency)}</div>
          {row.sessions && (
            <div className="price-per-session">
              {formatPrice(row.price / row.sessions, row.currency)}/sesión
            </div>
          )}
          <div className="price-category">
            {getPriceCategoryIndicator(row.price)}
          </div>
        </div>
      )
    },
    {
      key: 'sessions',
      label: 'Jornadas',
      sortable: true,
      render: (row) => (
        <div className="sessions-info">
          <div className="sessions-count">
            <span className="material-icons">event</span>
            <span className="sessions-number">{row.sessions}</span>
          </div>
          {row.sessionWindow && row.sessionWindow !== row.sessions && (
            <div className="session-window">
              <span className="material-icons">schedule</span>
              {row.sessionWindow} días
            </div>
          )}
          {row.postponements > 0 && (
            <div className="postponements">
              <span className="material-icons">update</span>
              {row.postponements} posposiciones
            </div>
          )}
        </div>
      )
    },
    {
      key: 'category',
      label: 'Categoría',
      sortable: true,
      render: (row) => (
        <div className="category-info">
          {getCategoryBadge(row.category)}
          {(row.minAge || row.maxAge) && (
            <div className="age-range">
              <span className="material-icons">child_care</span>
              {getAgeRangeText(row.minAge, row.maxAge)}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'active',
      label: 'Estado',
      sortable: true,
      render: (row) => (
        <div className="status-info">
          <span className={`status-badge ${row.active ? 'status-active' : 'status-inactive'}`}>
            <span className="material-icons">
              {row.active ? 'check_circle' : 'cancel'}
            </span>
            {row.active ? 'Activo' : 'Inactivo'}
          </span>
          {row.availableFrom && new Date(row.availableFrom) > new Date() && (
            <div className="availability-info">
              <span className="material-icons">schedule</span>
              Disponible desde {new Date(row.availableFrom).toLocaleDateString('es-ES')}
            </div>
          )}
          {row.availableUntil && new Date(row.availableUntil) < new Date() && (
            <div className="availability-info expired">
              <span className="material-icons">event_busy</span>
              Expirado
            </div>
          )}
        </div>
      )
    },
    {
      key: 'stats',
      label: 'Estadísticas',
      render: (row) => (
        <div className="stats-info">
          <div className="stat-item">
            <span className="material-icons">shopping_cart</span>
            <span className="stat-value">{row.totalPurchases || 0}</span>
            <span className="stat-label">compras</span>
          </div>
          {row.totalRevenue && (
            <div className="stat-item revenue">
              <span className="material-icons">euro</span>
              <span className="stat-value">{formatPrice(row.totalRevenue, row.currency)}</span>
              <span className="stat-label">ingresos</span>
            </div>
          )}
          <div className="performance-indicator">
            {getPerformanceIndicator(row)}
          </div>
        </div>
      )
    }
  ];

  /**
   * Configuración de campos para el formulario
   */
  const createFormFields = [
    {
      key: 'name',
      label: 'Nombre del Pack',
      type: 'text',
      required: true,
      placeholder: 'Ej: Pack 5 Jornadas Benjamín'
    },
    {
      key: 'torneoId',
      label: 'Torneo',
      type: 'select',
      required: true,
      options: [
        {
          value: '',
          label: referenceLoading ? 'Cargando torneos...' : 'Selecciona un torneo'
        },
        ...torneoOptions
      ]
    },
    {
      key: 'sedeId',
      label: 'Sede',
      type: 'select',
      required: true,
      disabled: !formData.torneoId || referenceLoading,
      options: [
        {
          value: '',
          label: !formData.torneoId
            ? 'Selecciona antes un torneo'
            : referenceLoading
              ? 'Cargando sedes...'
              : sedeOptions.length > 0
                ? 'Selecciona una sede'
                : 'No hay sedes asociadas a este torneo'
        },
        ...sedeOptions
      ]
    },
    {
      key: 'sessions',
      label: 'Número de Jornadas',
      type: 'number',
      required: true,
      min: 1
    },
    {
      key: 'price',
      label: 'Precio (€)',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01
    },
    {
      key: 'description',
      label: 'Descripción (opcional)',
      type: 'textarea',
      placeholder: 'Si lo dejas vacío, se generará una descripción por defecto'
    },
    {
      key: 'includesEquipment',
      label: 'Incluye Equipamiento',
      type: 'checkbox'
    },
    {
      key: 'availableFrom',
      label: 'Disponible Desde',
      type: 'date'
    },
    {
      key: 'availableUntil',
      label: 'Disponible Hasta',
      type: 'date'
    },
    {
      key: 'tipo',
      label: 'Tipo de paquete',
      type: 'select',
      options: [
        { value: 'normal', label: 'Normal' },
        { value: 'primera_experiencia', label: '⚡ Primera Experiencia (1 compra, descuento equipación)' }
      ],
      defaultValue: 'normal',
      help: 'Primera Experiencia: solo se puede comprar una vez. Activa -3€ en equipación en compras futuras.'
    },
    {
      key: 'active',
      label: 'Activo',
      type: 'checkbox',
      defaultValue: true
    }
  ];

  const editFormFields = [
    // Información básica
    {
      key: 'name',
      label: 'Nombre del Producto',
      type: 'text',
      required: true,
      placeholder: 'Ej: Pack Básico de Fútbol'
    },
    {
      key: 'code',
      label: 'Código',
      type: 'text',
      required: true,
      placeholder: 'Ej: P1, P2, P3...',
      help: 'Formato: P seguido de número (P1, P2, etc.)'
    },
    {
      key: 'description',
      label: 'Descripción',
      type: 'textarea',
      required: true,
      placeholder: 'Descripción detallada del producto...'
    },

    // Precio y configuración
    {
      key: 'price',
      label: 'Precio (€)',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01
    },
    {
      key: 'currency',
      label: 'Moneda',
      type: 'select',
      required: true,
      options: [
        { value: 'EUR', label: 'Euro (€)' },
        { value: 'USD', label: 'Dólar ($)' }
      ],
      defaultValue: 'EUR'
    },
    {
      key: 'sessions',
      label: 'Número de Jornadas',
      type: 'number',
      required: true,
      min: 1
    },
    {
      key: 'sessionWindow',
      label: 'Ventana de Sesiones (días)',
      type: 'number',
      min: 1,
      help: 'Período en días para usar las sesiones'
    },

    // Configuración adicional
    {
      key: 'postponements',
      label: 'Posposiciones Permitidas',
      type: 'number',
      min: 0,
      max: 10,
      defaultValue: 0
    },
    {
      key: 'category',
      label: 'Categoría',
      type: 'select',
      required: true,
      options: [
        { value: 'individual', label: 'Individual' },
        { value: 'pack', label: 'Pack' },
        { value: 'premium', label: 'Premium' },
        { value: 'special', label: 'Especial' }
      ],
      defaultValue: 'pack'
    },

    // Equipamiento
    {
      key: 'includesEquipment',
      label: 'Incluye Equipamiento',
      type: 'checkbox'
    },
    {
      key: 'equipmentDetails',
      label: 'Detalles del Equipamiento',
      type: 'textarea',
      placeholder: 'Descripción del equipamiento incluido...',
      conditional: { field: 'includesEquipment', value: true }
    },

    // Restricciones de edad
    {
      key: 'minAge',
      label: 'Edad Mínima',
      type: 'number',
      min: 4,
      max: 18
    },
    {
      key: 'maxAge',
      label: 'Edad Máxima',
      type: 'number',
      min: 4,
      max: 18
    },

    // Disponibilidad
    {
      key: 'availableFrom',
      label: 'Disponible Desde',
      type: 'date'
    },
    {
      key: 'availableUntil',
      label: 'Disponible Hasta',
      type: 'date'
    },

    // Configuración avanzada
    {
      key: 'tipo',
      label: 'Tipo de paquete',
      type: 'select',
      options: [
        { value: 'normal', label: 'Normal' },
        { value: 'primera_experiencia', label: '⚡ Primera Experiencia (1 compra, descuento equipación)' }
      ],
      defaultValue: 'normal',
      help: 'Primera Experiencia: solo se puede comprar una vez. Activa -3€ en equipación en compras futuras.'
    },
    {
      key: 'maxPurchasesPerUser',
      label: 'Máximo por Usuario',
      type: 'number',
      min: 1,
      help: 'Límite de compras por usuario (vacío = sin límite). Primera Experiencia lo fija automáticamente a 1.'
    },
    {
      key: 'priority',
      label: 'Prioridad',
      type: 'number',
      min: 0,
      defaultValue: 0,
      help: 'Mayor número = mayor prioridad en listados'
    },

    // Estados
    {
      key: 'active',
      label: 'Activo',
      type: 'checkbox',
      defaultValue: true
    },
    {
      key: 'featured',
      label: 'Destacado',
      type: 'checkbox'
    }
  ];

  const loadTournamentOptions = useCallback(async () => {
    try {
      setReferenceLoading(true);
      const response = await torneoService.getTorneos();
      const torneos = response.data || [];

      setTorneoOptions(
        torneos.map((torneo) => ({
          value: String(torneo.id),
          label: torneo.nombre
        }))
      );
    } catch (err) {
      console.error('❌ [Admin] Error al cargar torneos para productos:', err);
      setError('Error al cargar los torneos para crear el paquete.');
    } finally {
      setReferenceLoading(false);
    }
  }, []);

  const loadSedesForTorneo = useCallback(async (torneoId) => {
    if (!torneoId) {
      setSedeOptions([]);
      return;
    }

    try {
      setReferenceLoading(true);
      const response = await torneoService.getTorneo(torneoId);
      const sedes = response.data?.sedesEnTorneo || [];

      setSedeOptions(
        sedes
          .filter((item) => item.sede?.id)
          .map((item) => ({
            value: String(item.sede.id),
            label: item.sede.poblacion
              ? `${item.sede.nombre} (${item.sede.poblacion})`
              : item.sede.nombre
          }))
      );
    } catch (err) {
      console.error('❌ [Admin] Error al cargar sedes del torneo:', err);
      setSedeOptions([]);
      setFormErrors((prev) => ({
        ...prev,
        general: 'No se pudieron cargar las sedes del torneo seleccionado.'
      }));
    } finally {
      setReferenceLoading(false);
    }
  }, []);

  /**
   * Cargar datos iniciales
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await productService.getAll({
        ...filters,
        limit: 100,
      });
      console.log('📦 [Admin] Productos cargados:', response.data?.length || 0);

      const products = response.data || [];

      // 🔄 Cargar estadísticas reales desde PostgreSQL para cada producto
      const productsWithRealStats = await Promise.all(
        products.map(async (product) => {
          try {
            const statsResponse = await productService.getRealStats(product._id);
            return {
              ...product,
              totalPurchases: statsResponse.data.totalPurchases,
              totalRevenue: statsResponse.data.totalRevenue,
              statsSource: statsResponse.data.source
            };
          } catch (error) {
            console.warn(`⚠️ No se pudieron cargar estadísticas para ${product.code}:`, error);
            // Fallback a datos de MongoDB
            return product;
          }
        })
      );

      setData(productsWithRealStats);
    } catch (err) {
      console.error('❌ [Admin] Error al cargar productos:', err);
      setError('Error al cargar los productos. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Efecto para cargar datos iniciales
   */
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Manejar cambios en los filtros
   */
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  /**
   * Limpiar todos los filtros
   */
  const clearFilters = () => {
    setFilters({
      search: '',
      active: '',
      featured: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      includesEquipment: '',
      hasAgeRestrictions: '',
      performance: '',
      availability: ''
    });
  };

  /**
   * Formatear precio
   */
  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  /**
   * Obtener badge de categoría
   */
  const getCategoryBadge = (category) => {
    const categoryConfig = {
      individual: {
        bg: 'info',
        text: 'Individual',
        icon: 'person',
        color: '#2196f3'
      },
      pack: {
        bg: 'primary',
        text: 'Pack',
        icon: 'inventory_2',
        color: '#1976d2'
      },
      premium: {
        bg: 'warning',
        text: 'Premium',
        icon: 'star',
        color: '#ff9800'
      },
      special: {
        bg: 'success',
        text: 'Especial',
        icon: 'diamond',
        color: '#4caf50'
      }
    };

    const config = categoryConfig[category] || {
      bg: 'secondary',
      text: category,
      icon: 'category',
      color: '#9e9e9e'
    };

    return (
      <span className={`badge category-badge category-${category}`}>
        <span className="material-icons">{config.icon}</span>
        {config.text}
      </span>
    );
  };

  /**
   * Obtener indicador de categoría de precio
   */
  const getPriceCategoryIndicator = (price) => {
    if (price < 20) {
      return <span className="price-indicator low">Económico</span>;
    } else if (price < 50) {
      return <span className="price-indicator medium">Estándar</span>;
    } else {
      return <span className="price-indicator high">Premium</span>;
    }
  };

  /**
   * Obtener texto del rango de edad
   */
  const getAgeRangeText = (minAge, maxAge) => {
    if (minAge && maxAge) {
      return `${minAge}-${maxAge} años`;
    } else if (minAge) {
      return `+${minAge} años`;
    } else if (maxAge) {
      return `≤${maxAge} años`;
    }
    return 'Todas las edades';
  };

  /**
   * Obtener indicador de rendimiento
   */
  const getPerformanceIndicator = (product) => {
    const purchases = product.totalPurchases || 0;

    if (purchases === 0) {
      return <span className="performance-indicator none">Sin ventas</span>;
    } else if (purchases < 5) {
      return <span className="performance-indicator low">Bajo</span>;
    } else if (purchases < 20) {
      return <span className="performance-indicator medium">Medio</span>;
    } else {
      return <span className="performance-indicator high">Alto</span>;
    }
  };

  /**
   * Abrir modal de creación
   */
  const openCreateModal = async () => {
    if (torneoOptions.length === 0) {
      await loadTournamentOptions();
    }

    // Abrir modal de creación
    setCurrentItem(null);
    setSedeOptions([]);
    setFormData({
      name: '',
      torneoId: '',
      sedeId: '',
      description: '',
      price: '',
      sessions: '',
      includesEquipment: false,
      active: true,
      availableFrom: '',
      availableUntil: '',
      tipo: 'normal'
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  /**
   * Abrir modal de edición
   */
  const openEditModal = (item) => {
    // Abrir modal de edición
    setCurrentItem(item);
    setFormData({
      name: item.name || '',
      code: item.code || '',
      description: item.description || '',
      price: item.price || '',
      currency: item.currency || 'EUR',
      sessions: item.sessions || '',
      sessionWindow: item.sessionWindow || '',
      postponements: item.postponements || 0,
      includesEquipment: item.includesEquipment || false,
      equipmentDetails: item.equipmentDetails || '',
      active: item.active !== undefined ? item.active : true,
      featured: item.featured || false,
      availableFrom: item.availableFrom ? item.availableFrom.split('T')[0] : '',
      availableUntil: item.availableUntil ? item.availableUntil.split('T')[0] : '',
      maxPurchasesPerUser: item.maxPurchasesPerUser || '',
      minAge: item.minAge || '',
      maxAge: item.maxAge || '',
      category: item.category || 'pack',
      priority: item.priority || 0,
      tipo: item.tipo || 'normal'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  /**
   * Abrir modal de visualización
   */
  const openViewModal = (item) => {
    // Abrir modal de visualización
    setCurrentItem(item);
    setShowViewModal(true);
  };

  /**
   * Abrir modal de eliminación
   */
  const openDeleteModal = (item) => {
    // Abrir modal de eliminación
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  /**
   * Cerrar todos los modales
   */
  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setCurrentItem(null);
    setFormData({});
    setSedeOptions([]);
    setFormErrors({});
    setFormLoading(false);
  };

  /**
   * Validar datos del formulario
   */
  const validateData = (data, mode = 'edit') => {
    const errors = {};

    // Validaciones básicas
    if (!data.name?.trim()) {
      errors.name = 'El nombre es obligatorio';
    }

    if (mode === 'create') {
      if (!data.torneoId) {
        errors.torneoId = 'Debes seleccionar un torneo';
      }

      if (!data.sedeId) {
        errors.sedeId = 'Debes seleccionar una sede';
      }
    } else {
      if (!data.code?.trim()) {
        errors.code = 'El código es obligatorio';
      } else if (!/^P\d+$/i.test(data.code.trim())) {
        errors.code = 'El código debe seguir el formato P1, P2, P3, etc.';
      }

      if (!data.description?.trim()) {
        errors.description = 'La descripción es obligatoria';
      }
    }

    if (!data.price || parseFloat(data.price) <= 0) {
      errors.price = 'El precio debe ser mayor a 0';
    }

    if (!data.sessions || parseInt(data.sessions) < 1) {
      errors.sessions = 'Debe incluir al menos 1 jornada';
    }

    // Validaciones condicionales
    if (data.sessionWindow && parseInt(data.sessionWindow) < parseInt(data.sessions)) {
      errors.sessionWindow = 'La ventana de sesiones debe ser mayor o igual al número de sesiones';
    }

    if (data.postponements && parseInt(data.postponements) < 0) {
      errors.postponements = 'Las posposiciones no pueden ser negativas';
    }

    if (data.maxAge && data.minAge && parseInt(data.maxAge) < parseInt(data.minAge)) {
      errors.maxAge = 'La edad máxima debe ser mayor o igual a la edad mínima';
    }

    if (data.availableUntil && data.availableFrom &&
        new Date(data.availableUntil) <= new Date(data.availableFrom)) {
      errors.availableUntil = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  /**
   * Transformar datos del formulario para el backend
   */
  const transformFormData = (formData) => {
    const transformed = { ...formData };

    // Convertir strings a números donde sea necesario
    if (transformed.price) transformed.price = parseFloat(transformed.price);
    if (transformed.sessions) transformed.sessions = parseInt(transformed.sessions);
    if (transformed.torneoId) transformed.torneoId = parseInt(transformed.torneoId);
    if (transformed.sedeId) transformed.sedeId = parseInt(transformed.sedeId);
    if (transformed.sessionWindow) transformed.sessionWindow = parseInt(transformed.sessionWindow);
    if (transformed.postponements) transformed.postponements = parseInt(transformed.postponements);
    if (transformed.priority) transformed.priority = parseInt(transformed.priority);
    if (transformed.minAge) transformed.minAge = parseInt(transformed.minAge);
    if (transformed.maxAge) transformed.maxAge = parseInt(transformed.maxAge);
    if (transformed.maxPurchasesPerUser) transformed.maxPurchasesPerUser = parseInt(transformed.maxPurchasesPerUser);

    // Limpiar campos vacíos opcionales
    Object.keys(transformed).forEach(key => {
      if (transformed[key] === '' || transformed[key] === null) {
        delete transformed[key];
      }
    });

    // Asegurar que el código esté en mayúsculas
    if (transformed.code) {
      transformed.code = transformed.code.toUpperCase();
    }

    return transformed;
  };

  /**
   * Manejar creación de producto
   */
  const handleCreate = async (e) => {
    e.preventDefault();

    const validation = validateData(formData, 'create');
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    try {
      setFormLoading(true);
      setFormErrors({});

      const transformedData = transformFormData(formData);
      await productService.create(transformedData);

      closeAllModals();
      await loadData();

    } catch (err) {
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      } else {
        setFormErrors({
          general: err.response?.data?.message || 'Error al crear el producto'
        });
      }
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Manejar actualización de producto
   */
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!currentItem?._id) {
      return;
    }

    const validation = validateData(formData, 'edit');
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    try {
      setFormLoading(true);
      setFormErrors({});

      const transformedData = transformFormData(formData);
      await productService.update(currentItem._id, transformedData);

      closeAllModals();
      await loadData();

    } catch (err) {
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      } else {
        setFormErrors({
          general: err.response?.data?.message || 'Error al actualizar el producto'
        });
      }
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Manejar eliminación de producto
   */
  const handleDelete = async () => {
    if (!currentItem?._id) {
      return;
    }

    try {
      setFormLoading(true);
      await productService.delete(currentItem._id);
      closeAllModals();
      await loadData();
    } catch (err) {
      // Error handling could be improved with toast notifications
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Manejar cambios en el formulario
   */
  const handleFormChange = async (field, value) => {
    if ((showCreateModal || showEditModal) && field === 'torneoId') {
      setFormData(prev => ({
        ...prev,
        torneoId: value,
        sedeId: ''
      }));

      setFormErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.torneoId;
        delete newErrors.sedeId;
        delete newErrors.general;
        return newErrors;
      });

      await loadSedesForTorneo(value);
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    if (formErrors[field] || formErrors.general) {
      setFormErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[field];
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  /**
   * Manejar activar/desactivar producto
   */
  const handleToggleActive = async (item) => {
    try {
      setFormLoading(true);
      const newStatus = !item.active;

      await productService.update(item._id, { active: newStatus });

      // Actualizar el estado local
      setData(prevData =>
        prevData.map(product =>
          product._id === item._id
            ? { ...product, active: newStatus }
            : product
        )
      );

      console.log(`✅ [Admin] Producto ${newStatus ? 'activado' : 'desactivado'}:`, item.name);
    } catch (err) {
      console.error('❌ [Admin] Error al cambiar estado del producto:', err);
      setError('Error al cambiar el estado del producto. Por favor, inténtalo de nuevo.');
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Manejar destacar/no destacar producto
   */
  const handleToggleFeatured = async (item) => {
    try {
      setFormLoading(true);
      const newFeaturedStatus = !item.featured;

      await productService.update(item._id, { featured: newFeaturedStatus });

      // Actualizar el estado local
      setData(prevData =>
        prevData.map(product =>
          product._id === item._id
            ? { ...product, featured: newFeaturedStatus }
            : product
        )
      );

      console.log(`✅ [Admin] Producto ${newFeaturedStatus ? 'destacado' : 'no destacado'}:`, item.name);
    } catch (err) {
      console.error('❌ [Admin] Error al cambiar estado destacado:', err);
      setError('Error al cambiar el estado destacado. Por favor, inténtalo de nuevo.');
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Duplicar producto
   */
  const handleDuplicateProduct = async (item) => {
    if (torneoOptions.length === 0) {
      await loadTournamentOptions();
    }

    const duplicatedData = {
      name: `${item.name} (Copia)`,
      torneoId: '',
      sedeId: '',
      description: item.description || '',
      price: item.price || '',
      sessions: item.sessions || '',
      includesEquipment: item.includesEquipment || false,
      active: false,
      availableFrom: item.availableFrom ? item.availableFrom.split('T')[0] : '',
      availableUntil: item.availableUntil ? item.availableUntil.split('T')[0] : ''
    };

    setSedeOptions([]);
    setFormData(duplicatedData);
    setFormErrors({});
    setCurrentItem(null);
    setShowCreateModal(true);
  };

  /**
   * Configuración de acciones para la tabla
   */
  const actions = [
    {
      key: 'view',
      icon: 'visibility',
      label: 'Ver detalles',
      type: 'default',
      onClick: openViewModal
    },
    {
      key: 'edit',
      icon: 'edit',
      label: 'Editar',
      type: 'primary',
      onClick: openEditModal
    },
    {
      key: 'duplicate',
      icon: 'content_copy',
      label: 'Duplicar',
      type: 'info',
      onClick: handleDuplicateProduct
    },
    {
      key: 'toggle',
      icon: (row) => row.active ? 'visibility_off' : 'visibility',
      label: (row) => row.active ? 'Desactivar' : 'Activar',
      type: (row) => row.active ? 'warning' : 'success',
      onClick: handleToggleActive
    },
    {
      key: 'featured',
      icon: (row) => row.featured ? 'star' : 'star_border',
      label: (row) => row.featured ? 'Quitar destacado' : 'Destacar',
      type: (row) => row.featured ? 'secondary' : 'warning',
      onClick: handleToggleFeatured
    },
    {
      key: 'delete',
      icon: 'delete',
      label: 'Eliminar',
      type: 'danger',
      onClick: openDeleteModal,
      condition: (row) => (row.totalPurchases || 0) === 0 // Solo permitir eliminar si no tiene compras
    }
  ];

  /**
   * Exportar productos a CSV
   */
  const handleExportProducts = () => {
    try {
      const csvData = data.map(product => ({
        Código: product.code,
        Nombre: product.name,
        Descripción: product.description,
        Precio: product.price,
        Moneda: product.currency,
        Jornadas: product.sessions,
        Categoría: product.category,
        Estado: product.active ? 'Activo' : 'Inactivo',
        Destacado: product.featured ? 'Sí' : 'No',
        'Incluye Equipamiento': product.includesEquipment ? 'Sí' : 'No',
        'Total Compras': product.totalPurchases || 0,
        'Ingresos Totales': product.totalRevenue || 0,
        'Fecha Creación': product.createdAt ? new Date(product.createdAt).toLocaleDateString('es-ES') : ''
      }));

      const csvContent = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `productos_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ [Admin] Productos exportados a CSV');
    } catch (err) {
      console.error('❌ [Admin] Error al exportar productos:', err);
      setError('Error al exportar los productos. Por favor, inténtalo de nuevo.');
    }
  };

  /**
   * Botones de acción principal
   */
  const headerActions = (
    <div className="header-actions-group">
      <div className="action-buttons">
        <button
          className="btn btn-outline-secondary"
          onClick={clearFilters}
          title="Limpiar filtros"
        >
          <span className="material-icons">filter_alt_off</span>
          Limpiar Filtros
        </button>

        <button
          className="btn btn-outline-primary"
          onClick={handleExportProducts}
          disabled={data.length === 0}
          title="Exportar productos a CSV"
        >
          <span className="material-icons">download</span>
          Exportar
        </button>

        <button
          className="btn btn-primary"
          onClick={openCreateModal}
        >
          <span className="material-icons">add</span>
          Añadir Producto
        </button>
      </div>

      {data.length > 0 && (
        <div className="summary-info">
          <span className="summary-item">
            <span className="material-icons">inventory_2</span>
            {data.length} productos
          </span>
          <span className="summary-item">
            <span className="material-icons">check_circle</span>
            {data.filter(p => p.active).length} activos
          </span>
          <span className="summary-item">
            <span className="material-icons">star</span>
            {data.filter(p => p.featured).length} destacados
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="product-management-unified">
      <AdminCRUDLayout
        title="Gestión de Productos"
        subtitle="Administra los productos y packs disponibles en la plataforma"
        headerActions={headerActions}
        loading={loading}
        error={error}
      >
        <UnifiedDataTable
          data={data}
          columns={columns}
          actions={actions}
          filters={filterConfig}
          onFilterChange={handleFilterChange}
          loading={loading}
          emptyMessage="No se encontraron productos"
          searchPlaceholder="Buscar productos..."
        />
      </AdminCRUDLayout>

      {/* Modal de creación */}
      <StandardModal
        isOpen={showCreateModal}
        onClose={closeAllModals}
        title="Crear Nuevo Producto"
        size="large"
        type="create"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="product-create-form"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? 'Creando...' : 'Crear Producto'}
            </button>
          </>
        }
      >
        <StandardForm
          id="product-create-form"
          fields={createFormFields}
          data={formData}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          errors={formErrors}
          loading={formLoading || referenceLoading}
          submitLabel=""
          cancelLabel=""
          onCancel={null}
        />
      </StandardModal>

      {/* Modal de edición */}
      <StandardModal
        isOpen={showEditModal}
        onClose={closeAllModals}
        title="Editar Producto"
        size="large"
        type="edit"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="product-edit-form"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </>
        }
      >
        {currentItem?.sede && (
          <div className="edit-sede-banner">
            <span className="material-icons">location_on</span>
            <strong>Sede:</strong>&nbsp;{currentItem.sede.nombre}
            {currentItem.sede.poblacion ? ` (${currentItem.sede.poblacion})` : ''}
          </div>
        )}
        <StandardForm
          id="product-edit-form"
          fields={editFormFields}
          data={formData}
          onChange={handleFormChange}
          onSubmit={handleUpdate}
          errors={formErrors}
          loading={formLoading}
          submitLabel=""
          cancelLabel=""
          onCancel={null}
        />
      </StandardModal>

      {/* Modal de visualización */}
      <StandardModal
        isOpen={showViewModal}
        onClose={closeAllModals}
        title="Detalles del Producto"
        size="large"
        type="view"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                closeAllModals();
                setTimeout(() => openEditModal(currentItem), 100);
              }}
            >
              Editar Producto
            </button>
          </>
        }
      >
        {currentItem ? (
          <div className="product-details-enhanced">
            {/* Header del producto */}
            <div className="product-header">
              <div className="product-title-section">
                <div className="product-code-display">
                  <span className="material-icons">inventory_2</span>
                  <span className="code">{currentItem.code}</span>
                </div>
                <h3 className="product-title">{currentItem.name}</h3>
                <p className="product-description-full">{currentItem.description}</p>
              </div>
              <div className="product-badges-section">
                {getCategoryBadge(currentItem.category)}
                {currentItem.featured && (
                  <span className="badge badge-featured-large">
                    <span className="material-icons">star</span>
                    Destacado
                  </span>
                )}
                {currentItem.includesEquipment && (
                  <span className="badge badge-equipment-large">
                    <span className="material-icons">sports_soccer</span>
                    Con Equipamiento
                  </span>
                )}
              </div>
            </div>

            {/* Información principal en cards */}
            <div className="details-grid">
              {/* Card de Precio */}
              <div className="detail-card price-card">
                <div className="card-header">
                  <span className="material-icons">euro</span>
                  <h6>Precio y Configuración</h6>
                </div>
                <div className="card-content">
                  <div className="price-display">
                    <span className="main-price">{formatPrice(currentItem.price, currentItem.currency)}</span>
                    {currentItem.sessions && (
                      <span className="price-per-session">
                        {formatPrice(currentItem.price / currentItem.sessions, currentItem.currency)} por sesión
                      </span>
                    )}
                  </div>
                  <div className="price-details">
                    <div className="detail-item">
                      <span className="material-icons">event</span>
                      <span>{currentItem.sessions} jornadas</span>
                    </div>
                    {currentItem.sessionWindow && (
                      <div className="detail-item">
                        <span className="material-icons">schedule</span>
                        <span>Ventana: {currentItem.sessionWindow} días</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="material-icons">update</span>
                      <span>{currentItem.postponements || 0} posposiciones</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card de Estado */}
              <div className="detail-card status-card">
                <div className="card-header">
                  <span className="material-icons">info</span>
                  <h6>Estado y Disponibilidad</h6>
                </div>
                <div className="card-content">
                  {currentItem.sede && (
                    <div className="detail-item sede-detail">
                      <span className="material-icons">location_on</span>
                      <span>
                        <strong>{currentItem.sede.nombre}</strong>
                        {currentItem.sede.poblacion ? ` (${currentItem.sede.poblacion})` : ''}
                      </span>
                    </div>
                  )}
                  <div className="status-display">
                    <span className={`status-badge-large ${currentItem.active ? 'active' : 'inactive'}`}>
                      <span className="material-icons">
                        {currentItem.active ? 'check_circle' : 'cancel'}
                      </span>
                      {currentItem.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  {currentItem.availableFrom && (
                    <div className="detail-item">
                      <span className="material-icons">event_available</span>
                      <span>Desde: {new Date(currentItem.availableFrom).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                  {currentItem.availableUntil && (
                    <div className="detail-item">
                      <span className="material-icons">event_busy</span>
                      <span>Hasta: {new Date(currentItem.availableUntil).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                  {currentItem.maxPurchasesPerUser && (
                    <div className="detail-item">
                      <span className="material-icons">person</span>
                      <span>Máximo {currentItem.maxPurchasesPerUser} por usuario</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Card de Estadísticas */}
              <div className="detail-card stats-card">
                <div className="card-header">
                  <span className="material-icons">analytics</span>
                  <h6>Estadísticas de Ventas</h6>
                </div>
                <div className="card-content">
                  <div className="stats-grid">
                    <div className="stat-box">
                      <span className="stat-number">{currentItem.totalPurchases || 0}</span>
                      <span className="stat-label">Compras Totales</span>
                    </div>
                    {currentItem.totalRevenue && (
                      <div className="stat-box">
                        <span className="stat-number">{formatPrice(currentItem.totalRevenue, currentItem.currency)}</span>
                        <span className="stat-label">Ingresos Totales</span>
                      </div>
                    )}
                    <div className="stat-box">
                      <span className="stat-indicator">{getPerformanceIndicator(currentItem)}</span>
                      <span className="stat-label">Rendimiento</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card de Restricciones (si aplica) */}
              {(currentItem.minAge || currentItem.maxAge || currentItem.includesEquipment) && (
                <div className="detail-card restrictions-card">
                  <div className="card-header">
                    <span className="material-icons">rule</span>
                    <h6>Restricciones y Extras</h6>
                  </div>
                  <div className="card-content">
                    {(currentItem.minAge || currentItem.maxAge) && (
                      <div className="detail-item">
                        <span className="material-icons">child_care</span>
                        <span>{getAgeRangeText(currentItem.minAge, currentItem.maxAge)}</span>
                      </div>
                    )}
                    {currentItem.includesEquipment && (
                      <div className="detail-item">
                        <span className="material-icons">sports_soccer</span>
                        <span>{currentItem.equipmentDetails || 'Equipamiento incluido'}</span>
                      </div>
                    )}
                    {currentItem.priority > 0 && (
                      <div className="detail-item">
                        <span className="material-icons">priority_high</span>
                        <span>Prioridad: {currentItem.priority}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Información adicional */}
            {(currentItem.createdAt || currentItem.updatedAt) && (
              <div className="metadata-section">
                <h6>Información del Sistema</h6>
                <div className="metadata-grid">
                  {currentItem.createdAt && (
                    <div className="metadata-item">
                      <span className="material-icons">add_circle</span>
                      <span>Creado: {new Date(currentItem.createdAt).toLocaleString('es-ES')}</span>
                    </div>
                  )}
                  {currentItem.updatedAt && (
                    <div className="metadata-item">
                      <span className="material-icons">edit</span>
                      <span>Modificado: {new Date(currentItem.updatedAt).toLocaleString('es-ES')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4">
            <p>Cargando detalles del producto...</p>
          </div>
        )}
      </StandardModal>

      {/* Modal de eliminación */}
      <StandardModal
        isOpen={showDeleteModal}
        onClose={closeAllModals}
        title="Confirmar Eliminación"
        type="delete"
        size="medium"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={formLoading}
            >
              {formLoading ? 'Eliminando...' : 'Eliminar Producto'}
            </button>
          </>
        }
      >
        {currentItem ? (
          <div className="delete-confirmation">
            <div className="warning-icon">
              <span className="material-icons">warning</span>
            </div>
            <p>
              ¿Estás seguro de que deseas eliminar el producto{' '}
              <strong>{currentItem.name}</strong>?
            </p>
            <p className="warning-text">
              Esta acción no se puede deshacer.
            </p>

            {currentItem.totalPurchases > 0 && (
              <div className="alert alert-warning">
                <strong>Advertencia:</strong> Este producto tiene {currentItem.totalPurchases} compras registradas.
                Eliminar el producto puede afectar el historial de compras de los usuarios.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4">
            <p>Cargando información del producto...</p>
          </div>
        )}
      </StandardModal>
    </div>
  );
};

export default ProductManagementUnified;
